// sqlqualify — scope-aware table/column resolution for the SQL editor's
// autocomplete. Pure (no CodeMirror / Solid), node-tested in sqlqualify.test.mjs.
//
// lang-sql's own qualified completion is case-sensitive and only knows aliases
// from a statement's top-level FROM, so `a.` inside a subquery, `UPDATE t a SET
// a.`, `TM_BARANG.` or a derived-table alias completed nothing. This module
// owns every `x.` / `x.y.` position instead, case-insensitively:
//   alias.        → the aliased table's columns (FROM / JOIN / UPDATE / DELETE /
//                   INSERT target, at any subquery depth — innermost scope first)
//   table.        → that table's columns (in the statement or the default db)
//   derived.      → `(SELECT …) d` / CTE: the columns its SELECT list produces
//   NEW. / OLD.   → the trigger table's columns (CREATE TRIGGER … ON t)
//   db.           → that database's tables and views
//   db.table.     → that table's columns
// and supplies the tables visible at the cursor for unqualified column
// completion (a subquery's own tables first, then the enclosing query's).
//
// `ns` is the lang-sql namespace from schema.js buildSqlSchema:
//   { dbName: { tableName: [{label, type, detail}] } }

const eqi = (a, b) => String(a).toLowerCase() === String(b).toLowerCase();

// Find a key of obj case-insensitively (exact match preferred).
function keyOf(obj, name) {
  if (!obj || name == null) return null;
  if (Object.prototype.hasOwnProperty.call(obj, name)) return name;
  const lw = String(name).toLowerCase();
  for (const k of Object.keys(obj)) if (k.toLowerCase() === lw) return k;
  return null;
}

// ── Tokenizer ───────────────────────────────────────────────────────────────
// Tokens carry char offsets and their paren group. String/comment bodies are
// skipped; `backtick` identifiers are unwrapped (quoted: true).
function tokenize(s) {
  const toks = [];
  const n = s.length;
  let i = 0;
  while (i < n) {
    const c = s[i], nx = s[i + 1];
    if (/\s/.test(c)) { i++; continue; }
    if (c === '-' && nx === '-' && (i + 2 >= n || s.charCodeAt(i + 2) <= 32)) { while (i < n && s[i] !== '\n') i++; continue; }
    if (c === '#') { while (i < n && s[i] !== '\n') i++; continue; }
    if (c === '/' && nx === '*' && s[i + 2] !== '!' && !(s[i + 2] === 'M' && s[i + 3] === '!')) {
      i += 2; while (i < n && !(s[i] === '*' && s[i + 1] === '/')) i++; i += 2; continue;
    }
    if (c === '/' && nx === '*') { i += s[i + 2] === 'M' ? 4 : 3; while (i < n && /[0-9]/.test(s[i])) i++; continue; } // /*!NNNNN body → body is code
    if (c === '*' && nx === '/') { i += 2; continue; }
    if (c === "'" || c === '"') {
      const q = c, from = i; i++;
      while (i < n) { if (s[i] === '\\') { i += 2; continue; } if (s[i] === q) { if (s[i + 1] === q) { i += 2; continue; } i++; break; } i++; }
      toks.push({ t: 'str', from, to: i });
      continue;
    }
    if (c === '`') {
      const from = i; i++; let v = '';
      while (i < n) { if (s[i] === '`') { if (s[i + 1] === '`') { v += '`'; i += 2; continue; } i++; break; } v += s[i]; i++; }
      toks.push({ t: 'id', v, quoted: true, from, to: i });
      continue;
    }
    if (/[A-Za-z0-9_$]/.test(c) || c.charCodeAt(0) > 127) {
      const from = i;
      while (i < n && (/[A-Za-z0-9_$]/.test(s[i]) || s.charCodeAt(i) > 127)) i++;
      toks.push({ t: 'id', v: s.slice(from, i), from, to: i });
      continue;
    }
    if ('(),.;*'.includes(c)) { toks.push({ t: c, from: i, to: i + 1 }); i++; continue; }
    toks.push({ t: 'op', from: i, to: i + 1 }); i++;
  }
  return toks;
}

const kw = (tok, ...words) => tok && tok.t === 'id' && !tok.quoted && words.includes(tok.v.toLowerCase());

// ── Scope tree ──────────────────────────────────────────────────────────────
// Every (…) is a group; group 0 is the whole text. A group's `items` are its
// direct tokens with each nested group collapsed to one {t:'grp', g} item.
function buildGroups(toks, textLen) {
  const groups = [{ id: 0, parent: -1, from: 0, to: textLen, items: [] }];
  const stack = [0];
  for (const tk of toks) {
    const cur = groups[stack[stack.length - 1]];
    if (tk.t === '(') {
      const g = { id: groups.length, parent: cur.id, from: tk.from, to: Infinity, items: [] }; // unclosed until its )
      groups.push(g);
      cur.items.push({ t: 'grp', g: g.id, from: tk.from, to: Infinity });
      stack.push(g.id);
    } else if (tk.t === ')') {
      if (stack.length > 1) {
        const g = groups[stack.pop()];
        g.to = tk.to;
        const owner = groups[g.parent].items;
        owner[owner.length - 1].to = tk.to;
      }
    } else {
      cur.items.push(tk);
    }
  }
  return groups;
}

const CLAUSE_END = new Set(['from', 'where', 'group', 'having', 'order', 'limit', 'union', 'procedure', 'into', 'for', 'lock',
  'window', 'set', 'on', 'using', 'values', 'value', 'select', 'partition', 'straight_join', 'intersect', 'except',
  'returning', 'offset', 'fetch', 'natural', 'when', 'then', 'else', 'end', 'do']);
const JOIN_KW = new Set(['join', 'inner', 'left', 'right', 'cross', 'natural', 'straight_join', 'full', 'outer']);
const INSERT_MODS = new Set(['into', 'ignore', 'low_priority', 'high_priority', 'delayed', 'quick']);

// readRef parses `[db.]table [[AS] alias]` or `(subquery) [AS] alias` at items[k].
function readRef(items, k) {
  const t = items[k];
  if (!t) return null;
  if (t.t === 'grp') {
    let j = k + 1, alias = null;
    if (kw(items[j], 'as')) j++;
    if (items[j] && items[j].t === 'id' && !CLAUSE_END.has(items[j].v.toLowerCase()) && !JOIN_KW.has(items[j].v.toLowerCase())) { alias = items[j].v; j++; }
    return { ref: { derived: t.g, alias, table: null, db: null }, next: j };
  }
  if (t.t !== 'id') return null;
  const lw = t.v.toLowerCase();
  if (!t.quoted && (CLAUSE_END.has(lw) || JOIN_KW.has(lw))) return null;
  let db = null, table = t.v, j = k + 1;
  if (items[j] && items[j].t === '.' && items[j + 1] && items[j + 1].t === 'id') { db = table; table = items[j + 1].v; j += 2; }
  let alias = null;
  if (kw(items[j], 'as')) {
    if (items[j + 1] && items[j + 1].t === 'id') { alias = items[j + 1].v; j += 2; }
  } else if (items[j] && items[j].t === 'id') {
    const av = items[j].v.toLowerCase();
    if (items[j].quoted || (!CLAUSE_END.has(av) && !JOIN_KW.has(av) && av !== 'use' && av !== 'force' && av !== 'ignore')) { alias = items[j].v; j++; }
  }
  return { ref: { db, table, alias, derived: null }, next: j };
}

// Split a group's items at top-level `;` into segments (a routine body or a
// multi-statement selection holds several statements in one group).
function segmentsOf(items) {
  const segs = [[]];
  for (const it of items) { if (it.t === ';') segs.push([]); else segs[segs.length - 1].push(it); }
  return segs;
}

// refsIn — the table references one segment introduces, plus its CTEs.
function refsIn(items) {
  const refs = [], ctes = [];
  const readList = (j) => {
    while (j < items.length) {
      const r = readRef(items, j);
      if (!r) break;
      refs.push(r.ref);
      j = r.next;
      if (items[j] && items[j].t === ',') { j++; continue; }
      break;
    }
    return j;
  };
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    if (it.t !== 'id' || it.quoted) continue;
    const w = it.v.toLowerCase();
    if (w === 'with') {
      // WITH [RECURSIVE] name [(cols)] AS (…) [, …]
      let j = i + 1;
      if (kw(items[j], 'recursive')) j++;
      while (items[j] && items[j].t === 'id') {
        const name = items[j].v; j++;
        let cols = null;
        if (items[j] && items[j].t === 'grp' && kw(items[j + 1], 'as')) { cols = items[j].g; j++; } // name (c1, c2) AS (…)
        if (!kw(items[j], 'as')) break;
        j++;
        if (!items[j] || items[j].t !== 'grp') break;
        ctes.push({ name, body: items[j].g, colsGroup: cols });
        j++;
        if (items[j] && items[j].t === ',') { j++; continue; }
        break;
      }
      i = j - 1;
    } else if (w === 'from') {
      i = readList(i + 1) - 1;
    } else if (JOIN_KW.has(w)) {
      // [NATURAL] [INNER|CROSS|LEFT|RIGHT|FULL] [OUTER] JOIN / STRAIGHT_JOIN —
      // only with an actual JOIN: LEFT(…) / RIGHT(…) are string functions.
      let j = i;
      while (kw(items[j], 'natural', 'inner', 'cross', 'left', 'right', 'full', 'outer')) j++;
      if (!kw(items[j], 'join', 'straight_join')) continue;
      const r = readRef(items, j + 1);
      if (r) { refs.push(r.ref); i = r.next - 1; }
    } else if (w === 'update' && !kw(items[i - 1], 'key', 'for')) { // not ON DUPLICATE KEY UPDATE / FOR UPDATE
      let j = i + 1;
      while (kw(items[j], 'low_priority', 'ignore')) j++;
      i = readList(j) - 1;
    } else if (w === 'insert' || (w === 'replace' && !kw(items[i - 1], 'or'))) { // not CREATE OR REPLACE …; REPLACE(…) reads no table
      let j = i + 1;
      while (items[j] && items[j].t === 'id' && INSERT_MODS.has(items[j].v.toLowerCase())) j++;
      const r = readRef(items, j);
      if (r && r.ref.table) { refs.push(r.ref); i = r.next - 1; }
    } else if (w === 'trigger') {
      // CREATE TRIGGER name {BEFORE|AFTER} {INSERT|UPDATE|DELETE} ON tbl
      for (let j = i + 1; j < Math.min(items.length, i + 12); j++) {
        if (kw(items[j], 'on')) {
          const r = readRef(items, j + 1);
          if (r && r.ref.table) refs.push({ ...r.ref, alias: null, trigger: true, pos: it.from });
          break;
        }
      }
    }
  }
  return { refs, ctes };
}

// ── Analysis ────────────────────────────────────────────────────────────────

// analyze(text, cursor) → { visible: [{ref, scope}], all: [...], ctes, groups }
// `visible` lists the refs in scope at the cursor, innermost first: the cursor's
// own group (its current `;` segment), then each enclosing group. `all` holds
// every ref anywhere (fallback for aliases typed before their FROM exists).
export function analyze(text, cursor) {
  const src = String(text || '');
  const toks = tokenize(src);
  const groups = buildGroups(toks, src.length);
  const per = groups.map((g) => {
    const segs = segmentsOf(g.items).map((items) => ({ items, ...refsIn(items) }));
    return { segs };
  });
  // innermost group containing the cursor
  let inner = 0;
  // groups open in document order, so the last one containing the cursor is the deepest
  for (const g of groups) if (g.id && cursor > g.from && cursor < g.to) inner = g.id;
  const visible = [], ctes = [];
  for (let gid = inner, child = null, scope = 0; gid >= 0; child = gid, gid = groups[gid].parent, scope++) {
    const segs = per[gid].segs;
    // the `;` segment holding the cursor (or the child group we came from):
    // the last non-empty segment starting at or before the anchor
    const anchor = child == null ? cursor : groups[child].from;
    let seg = segs[0];
    for (const s of segs) if (s.items.length && s.items[0].from <= anchor) seg = s;
    for (const r of seg.refs) visible.push({ ...r, scope });
    for (const c of seg.ctes) ctes.push(c);
  }
  const all = [];
  for (const p of per) for (const s of p.segs) { for (const r of s.refs) all.push(r); for (const c of s.ctes) ctes.push(c); }
  // memo/visiting: each derived group's columns are computed once per analysis,
  // and a CTE that reaches itself (WITH RECURSIVE) stops instead of re-expanding.
  return { visible, all, ctes, groups, per, memo: new Map(), visiting: new Set() };
}

// Columns a table ref produces: [{label, type, detail}].
// No depth cap: every recursion passes through derivedColumns, whose visiting
// set stops cycles, so each memoised result is complete.
function columnsOfRef(ref, a, ns, def, depth = 0) {
  if (ref.derived != null) return derivedColumns(ref.derived, a, ns, def, depth + 1);
  if (!ref.db) {
    const cte = a.ctes.find((c) => eqi(c.name, ref.table));
    // Inside its own body a CTE name means the CTE only when recursive; a plain
    // `WITH t AS (SELECT … FROM t)` reads the base table t — either way, while
    // the CTE is being expanded, fall back to the base table.
    if (cte && !a.visiting.has(cte.body)) return cteColumns(cte, a, ns, def, depth + 1);
  }
  return tableColumns(ns, ref.db || def, ref.table);
}

export function tableColumns(ns, db, table) {
  const dk = keyOf(ns, db);
  if (!dk) return [];
  const tk = keyOf(ns[dk], table);
  const cols = tk ? ns[dk][tk] : null;
  return Array.isArray(cols) ? cols : [];
}

function cteColumns(cte, a, ns, def, depth) {
  if (cte.colsGroup != null) {
    const names = a.groups[cte.colsGroup].items.filter((it) => it.t === 'id').map((it) => it.v);
    if (names.length) return names.map((n) => ({ label: n, type: 'property', detail: `${cte.name} (CTE)` }));
  }
  return derivedColumns(cte.body, a, ns, def, depth).map((c) => ({ ...c, detail: c.detail || `${cte.name} (CTE)` }));
}

// The columns a `(SELECT …)` group produces, read from its SELECT list: `x AS n`
// / `x n` → n, `t.col` / `col` → col, `*` / `t.*` → the underlying columns.
function derivedColumns(gid, a, ns, def, depth) {
  if (a.memo.has(gid)) return a.memo.get(gid);
  if (a.visiting.has(gid)) return [];
  a.visiting.add(gid);
  const out = derivedColumnsOf(gid, a, ns, def, depth);
  a.visiting.delete(gid);
  a.memo.set(gid, out);
  return out;
}

function derivedColumnsOf(gid, a, ns, def, depth) {
  const seg = a.per[gid].segs[0];
  const items = seg.items;
  let i = items.findIndex((it) => kw(it, 'select'));
  if (i < 0) {
    // (SELECT …) UNION … or ((SELECT …)) — use the first nested group
    const inner = items.find((it) => it.t === 'grp');
    return inner ? derivedColumns(inner.g, a, ns, def, depth + 1) : [];
  }
  i++;
  while (kw(items[i], 'distinct', 'all', 'distinctrow', 'straight_join', 'sql_calc_found_rows', 'sql_no_cache', 'sql_cache', 'high_priority')) i++;
  const out = [];
  const seen = new Set();
  const push = (c) => { const k = c.label.toLowerCase(); if (!seen.has(k)) { seen.add(k); out.push(c); } };
  const localRefs = seg.refs;
  let item = [];
  const flushItem = () => {
    const it = item; item = [];
    if (!it.length) return;
    const last = it[it.length - 1], prev = it[it.length - 2];
    if (last.t === '*') {
      if (prev && prev.t === '.' && it[it.length - 3] && it[it.length - 3].t === 'id') {
        const q = it[it.length - 3].v;
        const r = localRefs.find((x) => x.alias && eqi(x.alias, q)) || localRefs.find((x) => x.table && eqi(x.table, q));
        if (r) columnsOfRef(r, a, ns, def, depth).forEach(push);
      } else {
        for (const r of localRefs) columnsOfRef(r, a, ns, def, depth).forEach(push);
      }
      return;
    }
    if (last.t !== 'id') return;
    if (prev && kw(prev, 'as')) { push({ label: last.v, type: 'property' }); return; }
    if (it.length === 1 || (prev && prev.t === '.')) {
      // bare / qualified column — keep its type when we can find it
      let detail;
      if (prev && prev.t === '.' && it[it.length - 3]) {
        const q = it[it.length - 3].v;
        const r = localRefs.find((x) => x.alias && eqi(x.alias, q)) || localRefs.find((x) => x.table && eqi(x.table, q));
        const c = r && columnsOfRef(r, a, ns, def, depth).find((cc) => eqi(cc.label, last.v));
        detail = c && c.detail;
      } else {
        for (const r of localRefs) {
          const c = columnsOfRef(r, a, ns, def, depth).find((cc) => eqi(cc.label, last.v));
          if (c) { detail = c.detail; break; }
        }
      }
      push({ label: last.v, type: 'property', detail });
      return;
    }
    // `expr alias` (implicit alias): an identifier right after a non-operator
    if (prev && (prev.t === 'id' || prev.t === 'grp' || prev.t === 'str') && !kw(last, 'end')) push({ label: last.v, type: 'property' });
  };
  for (; i < items.length; i++) {
    const it = items[i];
    if (kw(it, 'from', 'into', 'where', 'union', 'limit', 'order', 'group', 'having')) break;
    if (it.t === ',') { flushItem(); continue; }
    item.push(it);
  }
  flushItem();
  return out;
}

// A name that isn't a plain identifier is inserted `backticked` (a hyphen, a
// space, a leading digit …), as lang-sql's own completion did.
const PLAIN_ID = /^[A-Za-z_][A-Za-z0-9_]*$/;
function quoted(opt) {
  return PLAIN_ID.test(opt.label) || opt.apply ? opt : { ...opt, apply: '`' + opt.label.replace(/`/g, '``') + '`' };
}

// ── Public API ──────────────────────────────────────────────────────────────

// Mask string/comment bodies (length-preserving), keeping backticks, so the
// cursor test below can tell code from a literal.
function inLiteral(text, pos) {
  let inS = false, inD = false, inLine = false, inBlock = false, inB = false;
  for (let i = 0; i < pos; i++) {
    const c = text[i], n = text[i + 1];
    if (inLine) { if (c === '\n') inLine = false; continue; }
    if (inBlock) { if (c === '*' && n === '/') { inBlock = false; i++; } continue; }
    if (inS) { if (c === '\\') i++; else if (c === "'") inS = false; continue; }
    if (inD) { if (c === '\\') i++; else if (c === '"') inD = false; continue; }
    if (inB) { if (c === '`') inB = false; continue; }
    if (c === '-' && n === '-' && (i + 2 >= text.length || text.charCodeAt(i + 2) <= 32)) { inLine = true; continue; }
    if (c === '#') { inLine = true; continue; }
    if (c === '/' && n === '*' && text[i + 2] !== '!' && !(text[i + 2] === 'M' && text[i + 3] === '!')) { inBlock = true; i++; continue; }
    if (c === "'") { inS = true; continue; }
    if (c === '"') { inD = true; continue; }
    if (c === '`') { inB = true; continue; }
  }
  return inS || inD || inLine || inBlock;
}

const SEG = '(?:`(?:[^`]|``)+`|[A-Za-z_$\\u0080-\\uffff][\\w$\\u0080-\\uffff]*)';
const QUAL_RE = new RegExp(`(?:^|[^\\w$.\`])(?:(${SEG})\\.)?(${SEG})\\.([\\w$]*)$`);
const unq = (s) => (s && s[0] === '`' ? s.slice(1, -1).replace(/``/g, '`') : s);

// endsWithQualifier — a cheap syntactic test (no literal scan) that `before`
// ends in `x.` / `x.y.` + a partial word. The editor's gates use it on a short
// window: a literal check there could misread a quote cut by the window.
export function endsWithQualifier(before) {
  const m = QUAL_RE.exec(String(before).slice(-300));
  return !!m && !(/^[0-9]/.test(m[2]) && !m[1]);
}

// qualifierAt — the `x.` / `x.y.` qualifier ending at the cursor, or null.
// Returns { quals: ['x'] | ['x','y'], partial, from } (from = start of partial).
export function qualifierAt(text, pos) {
  const before = String(text).slice(Math.max(0, pos - 300), pos);
  const m = QUAL_RE.exec(before);
  if (!m) return null;
  if (/^[0-9]/.test(m[2]) && !m[1]) return null;           // 1.5 is a number
  const dot = pos - m[3].length - 1;
  if (inLiteral(text, dot)) return null;
  const quals = m[1] ? [unq(m[1]), unq(m[2])] : [unq(m[2])];
  return { quals, partial: m[3], from: pos - m[3].length };
}

// qualifiedCompletions — the options for a qualified position, or null when the
// qualifier resolves to nothing known. `stmtText` is the statement at the
// cursor and `cursor` the offset within it; `docText` (optional) is searched
// for a CREATE TRIGGER header when the statement itself holds none.
export function qualifiedCompletions({ stmtText, cursor, ns, defaultSchema, triggerTable, docText, docCursor }) {
  if (!ns) return null;
  const q = qualifierAt(stmtText, cursor);
  if (!q) return null;
  const a = analyze(stmtText, cursor);
  const def = defaultSchema;
  const result = (options, kind) => (options && options.length ? { from: q.from, options: options.map(quoted), kind } : null);

  if (q.quals.length === 2) {
    const [db, table] = q.quals;
    return result(tableColumns(ns, db, table), 'columns');
  }
  const x = q.quals[0];
  // 1) an alias / table in scope (innermost first), then anywhere in the statement
  // A match that resolves to no columns (e.g. the half-typed `FROM archive.`
  // itself reads as a table named archive) falls through to the later rules.
  for (const pool of [a.visible, a.all]) {
    const byAlias = pool.find((r) => r.alias && eqi(r.alias, x));
    const aliasCols = byAlias ? columnsOfRef(byAlias, a, ns, def) : [];
    if (aliasCols.length) return result(aliasCols, 'columns');
    const byName = pool.find((r) => r.table && eqi(r.table, x) && !r.trigger);
    const nameCols = byName ? columnsOfRef(byName, a, ns, def) : [];
    if (nameCols.length) return result(nameCols, 'columns');
  }
  // 2) NEW. / OLD. inside a trigger body
  if (eqi(x, 'new') || eqi(x, 'old')) {
    // the nearest CREATE TRIGGER header before the cursor (a document can hold several)
    const nearest = (refs, at) => refs.filter((r) => r.trigger && r.pos <= at).pop();
    let trg = nearest(a.all, cursor);
    if (!trg && docText && docText !== stmtText) {
      trg = nearest(analyze(docText, 0).all, docCursor == null ? docText.length : docCursor);
    }
    const tt = trg ? trg : (triggerTable ? { table: triggerTable, db: null } : null);
    if (tt) return result(tableColumns(ns, tt.db || def, tt.table), 'columns');
  }
  // 3) a CTE name
  const cte = a.ctes.find((c) => eqi(c.name, x));
  if (cte) return result(cteColumns(cte, a, ns, def, 0), 'columns');
  // 4) a table of the default database (its FROM not typed yet)
  const cols = def ? tableColumns(ns, def, x) : [];
  // 5) a database → its tables
  const dk = keyOf(ns, x);
  const tables = dk ? Object.keys(ns[dk]).map((t) => ({ label: t, type: 'class', detail: dk })) : [];
  if (cols.length && tables.length) return result([...cols, ...tables], 'mixed');
  if (cols.length) return result(cols, 'columns');
  return result(tables, 'tables');
}

// visibleColumns — columns for an UNQUALIFIED expression position: the tables in
// scope at the cursor (the subquery's own first), deduplicated, each tagged with
// its table in `detail`. Returns [] when nothing in scope resolves.
export function visibleColumns({ stmtText, cursor, ns, defaultSchema }) {
  if (!ns) return [];
  const a = analyze(stmtText, cursor);
  const out = [];
  const seen = new Set();
  for (const r of a.visible) {
    if (r.trigger) continue;
    for (const c of columnsOfRef(r, a, ns, defaultSchema)) {
      const k = c.label.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(quoted({ ...c, boost: Math.max(1, 3 - r.scope) })); // innermost scope ranks highest
    }
  }
  return out;
}
